/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

.spectrum--large {
  --spectrum-global-dimension-scale-factor: 1.25;
  --spectrum-global-dimension-size-0: 0px;
  --spectrum-global-dimension-size-10: 1px;
  --spectrum-global-dimension-size-25: 2px;
  --spectrum-global-dimension-size-40: 4px;
  --spectrum-global-dimension-size-50: 5px;
  --spectrum-global-dimension-size-65: 6px;
  --spectrum-global-dimension-size-75: 8px;
  --spectrum-global-dimension-size-85: 9px;
  --spectrum-global-dimension-size-100: 10px;
  --spectrum-global-dimension-size-115: 11px;
  --spectrum-global-dimension-size-125: 13px;
  --spectrum-global-dimension-size-130: 14px;
  --spectrum-global-dimension-size-150: 15px;
  --spectrum-global-dimension-size-160: 16px;
  --spectrum-global-dimension-size-175: 18px;
  --spectrum-global-dimension-size-200: 20px;
  --spectrum-global-dimension-size-225: 22px;
  --spectrum-global-dimension-size-250: 25px;
  --spectrum-global-dimension-size-300: 30px;
  --spectrum-global-dimension-size-350: 35px;
  --spectrum-global-dimension-size-400: 40px;
  --spectrum-global-dimension-size-450: 45px;
  --spectrum-global-dimension-size-500: 50px;
  --spectrum-global-dimension-size-550: 56px;
  --spectrum-global-dimension-size-600: 60px;
  --spectrum-global-dimension-size-675: 68px;
  --spectrum-global-dimension-size-700: 70px;
  --spectrum-global-dimension-size-800: 80px;
  --spectrum-global-dimension-size-900: 90px;
  --spectrum-global-dimension-size-1000: 100px;
  --spectrum-global-dimension-size-1200: 120px;
  --spectrum-global-dimension-size-1250: 125px;
  --spectrum-global-dimension-size-1600: 160px;
  --spectrum-global-dimension-size-1700: 170px;
  --spectrum-global-dimension-size-2000: 200px;
  --spectrum-global-dimension-size-2400: 240px;
  --spectrum-global-dimension-size-3000: 300px;
  --spectrum-global-dimension-size-3400: 340px;
  --spectrum-global-dimension-size-3600: 360px;
  --spectrum-global-dimension-size-4600: 460px;
  --spectrum-global-dimension-size-5000: 500px;
  --spectrum-global-dimension-size-6000: 600px;
  --spectrum-global-dimension-font-size-25: 12px;
  --spectrum-global-dimension-font-size-50: 13px;
  --spectrum-global-dimension-font-size-75: 15px;
  --spectrum-global-dimension-font-size-100: 17px;
  --spectrum-global-dimension-font-size-150: 18px;
  --spectrum-global-dimension-font-size-200: 19px;
  --spectrum-global-dimension-font-size-300: 22px;
  --spectrum-global-dimension-font-size-400: 24px;
  --spectrum-global-dimension-font-size-500: 27px;
  --spectrum-global-dimension-font-size-600: 31px;
  --spectrum-global-dimension-font-size-700: 34px;
  --spectrum-global-dimension-font-size-800: 39px;
  --spectrum-global-dimension-font-size-900: 44px;
  --spectrum-global-dimension-font-size-1000: 49px;
  --spectrum-global-dimension-font-size-1100: 55px;
  --spectrum-global-dimension-font-size-1200: 62px;
  --spectrum-global-dimension-font-size-1300: 70px;
  --spectrum-actionbutton-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-emphasized-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-quiet-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-quiet-emphasized-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-actionbutton-quiet-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-barloader-large-border-radius: 4px;
  --spectrum-barloader-large-indeterminate-border-radius: 4px;
  --spectrum-barloader-large-over-background-border-radius: 4px;
  --spectrum-barloader-small-border-radius: 3px;
  --spectrum-barloader-small-indeterminate-border-radius: 3px;
  --spectrum-barloader-small-over-background-border-radius: 3px;
  --spectrum-breadcrumb-compact-item-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-breadcrumb-compact-button-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-breadcrumb-item-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-breadcrumb-button-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-breadcrumb-multiline-item-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-breadcrumb-multiline-button-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-cta-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-cta-min-width: 90px;
  --spectrum-button-cta-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-cta-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-over-background-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-over-background-min-width: 90px;
  --spectrum-button-over-background-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-over-background-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-primary-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-primary-min-width: 90px;
  --spectrum-button-primary-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-primary-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-quiet-over-background-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-quiet-over-background-min-width: 90px;
  --spectrum-button-quiet-over-background-touch-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-over-background-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-quiet-over-background-cursor-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-primary-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-quiet-primary-min-width: 90px;
  --spectrum-button-quiet-primary-touch-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-primary-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-quiet-primary-cursor-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-secondary-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-quiet-secondary-min-width: 90px;
  --spectrum-button-quiet-secondary-touch-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-secondary-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-quiet-secondary-cursor-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-warning-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-quiet-warning-min-width: 90px;
  --spectrum-button-quiet-warning-touch-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-quiet-warning-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-quiet-warning-cursor-hit-x: var(--spectrum-global-dimension-static-size-250);
  --spectrum-button-secondary-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-secondary-min-width: 90px;
  --spectrum-button-secondary-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-secondary-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-warning-text-padding-bottom: var(--spectrum-global-dimension-size-100);
  --spectrum-button-warning-min-width: 90px;
  --spectrum-button-warning-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-button-warning-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-checkbox-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-text-gap-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-text-gap-error-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-checkbox-emphasized-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-text-gap-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-text-gap-error-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-checkbox-quiet-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-text-gap-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-text-gap-error-indeterminate-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-checkbox-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-circleloader-medium-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-circleloader-medium-over-background-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-circleloader-small-border-size: 3px;
  --spectrum-circleloader-small-over-background-border-size: 3px;
  --spectrum-colorhandle-loupe-margin: var(--spectrum-global-dimension-static-size-100);
  --spectrum-colorloupe-colorhandle-gap: var(--spectrum-global-dimension-static-size-100);
  --spectrum-colorslider-touch-hit-y: var(--spectrum-global-dimension-size-85);
  --spectrum-colorslider-vertical-touch-hit-x: var(--spectrum-global-dimension-size-85);
  --spectrum-colorwheel-min-size: var(--spectrum-global-dimension-static-size-2600);
  --spectrum-colorwheel-touch-hit-outer: var(--spectrum-global-dimension-size-85);
  --spectrum-colorwheel-touch-hit-inner: var(--spectrum-global-dimension-size-85);
  --spectrum-cyclebutton-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-cyclebutton-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-dialog-confirm-max-width: var(--spectrum-global-dimension-static-size-5000);
  --spectrum-dialog-confirm-title-text-size: var(--spectrum-global-dimension-font-size-200);
  --spectrum-dialog-confirm-description-text-size: var(--spectrum-global-dimension-font-size-75);
  --spectrum-dialog-confirm-padding: var(--spectrum-global-dimension-static-size-300);
  --spectrum-dialog-confirm-description-margin-bottom: var(--spectrum-global-dimension-static-size-500);
  --spectrum-dialog-max-width: var(--spectrum-global-dimension-static-size-5000);
  --spectrum-dialog-title-text-size: var(--spectrum-global-dimension-font-size-200);
  --spectrum-dialog-content-text-size: var(--spectrum-global-dimension-font-size-75);
  --spectrum-dialog-padding: var(--spectrum-global-dimension-static-size-300);
  --spectrum-dialog-content-margin-bottom: var(--spectrum-global-dimension-static-size-500);
  --spectrum-dialog-destructive-max-width: var(--spectrum-global-dimension-static-size-5000);
  --spectrum-dialog-destructive-title-text-size: var(--spectrum-global-dimension-font-size-200);
  --spectrum-dialog-destructive-description-text-size: var(--spectrum-global-dimension-font-size-75);
  --spectrum-dialog-destructive-padding: var(--spectrum-global-dimension-static-size-300);
  --spectrum-dialog-destructive-description-margin-bottom: var(--spectrum-global-dimension-static-size-500);
  --spectrum-dialog-error-max-width: var(--spectrum-global-dimension-static-size-5000);
  --spectrum-dialog-error-title-text-size: var(--spectrum-global-dimension-font-size-200);
  --spectrum-dialog-error-description-text-size: var(--spectrum-global-dimension-font-size-75);
  --spectrum-dialog-error-padding: var(--spectrum-global-dimension-static-size-300);
  --spectrum-dialog-error-description-margin-bottom: var(--spectrum-global-dimension-static-size-500);
  --spectrum-dialog-info-max-width: var(--spectrum-global-dimension-static-size-5000);
  --spectrum-dialog-info-title-text-size: var(--spectrum-global-dimension-font-size-200);
  --spectrum-dialog-info-description-text-size: var(--spectrum-global-dimension-font-size-75);
  --spectrum-dialog-info-padding: var(--spectrum-global-dimension-static-size-300);
  --spectrum-dialog-info-description-margin-bottom: var(--spectrum-global-dimension-static-size-500);
  --spectrum-dropdown-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-dropdown-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-dropdown-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-dropdown-thumbnail-small-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-dropdown-thumbnail-small-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-fieldbutton-quiet-min-width: var(--spectrum-global-dimension-size-200);
  --spectrum-icon-arrow-down-small-height: 12px;
  --spectrum-icon-arrow-left-medium-height: 12px;
  --spectrum-icon-checkmark-medium-width: 16px;
  --spectrum-icon-checkmark-medium-height: 16px;
  --spectrum-icon-checkmark-small-width: 12px;
  --spectrum-icon-checkmark-small-height: 12px;
  --spectrum-icon-chevron-down-medium-width: 12px;
  --spectrum-icon-chevron-left-large-width: 16px;
  --spectrum-icon-chevron-left-medium-height: 12px;
  --spectrum-icon-chevron-right-large-width: 16px;
  --spectrum-icon-chevron-right-medium-height: 12px;
  --spectrum-icon-cross-large-width: 16px;
  --spectrum-icon-cross-large-height: 16px;
  --spectrum-icon-dash-small-width: 12px;
  --spectrum-icon-dash-small-height: 12px;
  --spectrum-icon-skip-left-width: 10px;
  --spectrum-icon-skip-left-height: 12px;
  --spectrum-icon-skip-right-width: 10px;
  --spectrum-icon-skip-right-height: 12px;
  --spectrum-icon-triplegripper-width: 12px;
  --spectrum-listitem-option-icon-size: var(--spectrum-global-dimension-static-size-200);
  --spectrum-listitem-option-icon-margin-top: var(--spectrum-global-dimension-static-size-50);
  --spectrum-listitem-option-height: var(--spectrum-global-dimension-static-size-600);
  --spectrum-listitem-option-icon-padding-y: var(--spectrum-global-dimension-static-size-200);
  --spectrum-listitem-thumbnail-option-icon-margin-top: var(--spectrum-global-dimension-static-size-50);
  --spectrum-listitem-thumbnail-option-icon-padding-y: var(--spectrum-global-dimension-static-size-200);
  --spectrum-selectlist-thumbnail-small-option-icon-margin-top: var(--spectrum-global-dimension-static-size-50);
  --spectrum-selectlist-thumbnail-small-option-icon-padding-y: var(--spectrum-global-dimension-static-size-200);
  --spectrum-selectlist-option-icon-size: var(--spectrum-global-dimension-static-size-200);
  --spectrum-selectlist-option-icon-padding-y: var(--spectrum-global-dimension-static-size-200);
  --spectrum-selectlist-option-icon-margin-top: var(--spectrum-global-dimension-static-size-50);
  --spectrum-selectlist-option-height: var(--spectrum-global-dimension-static-size-600);
  /* BEGIN RSP PATCH */
  /* This is not correct in DNA. */
  --spectrum-selectlist-option-padding-y: var(--spectrum-global-dimension-static-size-160);
  /* END RSP PATCH */
  --spectrum-selectlist-thumbnail-option-icon-padding-y: var(--spectrum-global-dimension-static-size-200);
  --spectrum-loader-bar-large-border-radius: 4px;
  --spectrum-loader-bar-large-over-background-border-radius: 4px;
  --spectrum-loader-bar-small-border-radius: 3px;
  --spectrum-loader-bar-small-over-background-border-radius: 3px;
  --spectrum-loader-circle-medium-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-loader-circle-medium-over-background-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-loader-circle-small-border-size: 3px;
  --spectrum-loader-circle-small-over-background-border-size: 3px;
  --spectrum-meter-large-border-radius: 4px;
  --spectrum-meter-small-border-radius: 3px;
  --spectrum-pagination-page-button-line-height: 32px;
  /* BEGIN RSP PATCH */
  /* fix popover in edge https://github.com/adobe/react-spectrum/pull/402 */
  --spectrum-overlay-positive-transform-distance: 8px;
  --spectrum-overlay-negative-transform-distance: -8px;
  /* END RSP PATCH */
  --spectrum-picker-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-picker-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-picker-quiet-min-width: var(--spectrum-global-dimension-size-200);
  --spectrum-picker-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-picker-thumbnail-small-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-picker-thumbnail-small-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-progressbar-large-border-radius: 4px;
  --spectrum-progressbar-large-indeterminate-border-radius: 4px;
  --spectrum-progressbar-large-over-background-border-radius: 4px;
  --spectrum-progressbar-small-border-radius: 3px;
  --spectrum-progressbar-small-indeterminate-border-radius: 3px;
  --spectrum-progressbar-small-over-background-border-radius: 3px;
  --spectrum-progresscircle-medium-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-progresscircle-medium-over-background-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-progresscircle-small-border-size: 3px;
  --spectrum-progresscircle-small-indeterminate-border-size: 3px;
  --spectrum-progresscircle-small-over-background-border-size: 3px;
  --spectrum-progresscircle-medium-indeterminate-border-size: var(--spectrum-global-dimension-static-size-50);
  --spectrum-radio-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-margin-bottom: var(--spectrum-global-dimension-static-size-100);
  --spectrum-radio-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-radio-emphasized-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-emphasized-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-emphasized-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-emphasized-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-emphasized-margin-bottom: var(--spectrum-global-dimension-static-size-100);
  --spectrum-radio-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-radio-quiet-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-quiet-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-quiet-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-quiet-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-radio-quiet-margin-bottom: var(--spectrum-global-dimension-static-size-100);
  --spectrum-radio-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-rating-icon-width: 30px;
  --spectrum-rating-indicator-width: 20px;
  --spectrum-rating-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-rating-emphasized-icon-width: 30px;
  --spectrum-rating-emphasized-indicator-width: 20px;
  --spectrum-rating-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-rating-quiet-icon-width: 30px;
  --spectrum-rating-quiet-indicator-width: 20px;
  --spectrum-rating-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-search-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-search-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-search-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-search-quiet-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-search-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-search-quiet-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-sidenav-item-touch-hit-bottom: 3px;
  --spectrum-sidenav-multilevel-item-touch-hit-bottom: 3px;
  --spectrum-slider-track-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-slider-handle-touch-hit-x: var(--spectrum-global-dimension-static-size-175);
  --spectrum-slider-handle-touch-hit-y: var(--spectrum-global-dimension-static-size-175);
  --spectrum-slider-editable-track-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-slider-editable-handle-touch-hit-x: var(--spectrum-global-dimension-static-size-175);
  --spectrum-slider-editable-handle-touch-hit-y: var(--spectrum-global-dimension-static-size-175);
  --spectrum-slider-fill-track-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-slider-fill-handle-touch-hit-x: var(--spectrum-global-dimension-static-size-175);
  --spectrum-slider-fill-handle-touch-hit-y: var(--spectrum-global-dimension-static-size-175);
  --spectrum-switch-focus-ring-border-radius-key-focus: var(--spectrum-global-dimension-static-size-160);
  --spectrum-switch-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-track-width: var(--spectrum-global-dimension-static-size-450);
  --spectrum-switch-handle-border-radius: 9px;
  /* BEGIN RSP PATCH */
  /* fix switch in edge: https://github.com/adobe/react-spectrum/pull/90 */
  --spectrum-switch-handle-transform-x-ltr: 18px;
  --spectrum-switch-handle-transform-x-rtl: -18px;
  /* END RSP PATCH */
  --spectrum-switch-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-switch-emphasized-focus-ring-border-radius-key-focus: var(--spectrum-global-dimension-static-size-160);
  --spectrum-switch-emphasized-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-emphasized-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-emphasized-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-emphasized-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-emphasized-track-width: var(--spectrum-global-dimension-static-size-450);
  --spectrum-switch-emphasized-handle-border-radius: 9px;
  --spectrum-switch-emphasized-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-switch-quiet-focus-ring-border-radius-key-focus: var(--spectrum-global-dimension-static-size-160);
  --spectrum-switch-quiet-text-gap-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-quiet-text-gap-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-quiet-text-gap-error-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-quiet-text-gap-error-selected-key-focus: var(--spectrum-global-dimension-static-size-150);
  --spectrum-switch-quiet-track-width: var(--spectrum-global-dimension-static-size-450);
  --spectrum-switch-quiet-handle-border-radius: 9px;
  --spectrum-switch-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-tabs-compact-focus-ring-border-radius: 6px;
  --spectrum-tabs-compact-margin-left: -11px;
  --spectrum-tabs-compact-margin-right: -11px;
  --spectrum-tabs-compact-vertical-focus-ring-border-radius: 6px;
  --spectrum-tabs-compact-vertical-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-focus-ring-border-radius: 6px;
  --spectrum-tabs-margin-left: -11px;
  --spectrum-tabs-margin-right: -11px;
  --spectrum-tabs-emphasized-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-emphasized-margin-left: -11px;
  --spectrum-tabs-emphasized-margin-right: -11px;
  --spectrum-tabs-quiet-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-quiet-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-margin-left: -11px;
  --spectrum-tabs-quiet-margin-right: -11px;
  --spectrum-tabs-quiet-compact-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-compact-margin-left: -11px;
  --spectrum-tabs-quiet-compact-margin-right: -11px;
  --spectrum-tabs-quiet-compact-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-compact-emphasized-margin-left: -11px;
  --spectrum-tabs-quiet-compact-emphasized-margin-right: -11px;
  --spectrum-tabs-quiet-compact-vertical-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-compact-vertical-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-emphasized-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-quiet-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-emphasized-margin-left: -11px;
  --spectrum-tabs-quiet-emphasized-margin-right: -11px;
  --spectrum-tabs-quiet-vertical-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-quiet-vertical-focus-ring-border-radius: 6px;
  --spectrum-tabs-quiet-vertical-emphasized-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-quiet-vertical-emphasized-focus-ring-border-radius: 6px;
  --spectrum-tabs-vertical-baseline: var(--spectrum-global-dimension-size-250);
  --spectrum-tabs-vertical-focus-ring-border-radius: 6px;
  --spectrum-textarea-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textarea-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textarea-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-textarea-quiet-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textarea-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textarea-quiet-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-textfield-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textfield-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textfield-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-textfield-quiet-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textfield-quiet-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-textfield-quiet-icon-frame: var(--spectrum-global-dimension-static-size-500);
  --spectrum-tool-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-tool-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-tool-high-emphasis-touch-hit-x: var(--spectrum-global-dimension-static-size-50);
  --spectrum-tool-high-emphasis-touch-hit-y: var(--spectrum-global-dimension-static-size-50);
  --spectrum-tooltip-padding-bottom: 6px;
  /* BEGIN RSP PATCH */
  /* Old value is deprecated, bumping to new value for now: https://git.corp.adobe.com/Spectrum/spectrum-dna/blob/53f6efb0d81ec6829048a53a9e8389b450f46dee/data/elements/tooltip/TooltipBase.mjs#L36 */
  --spectrum-tooltip-content-max-width: var(--spectrum-global-dimension-size-2000);
  /* END RSP PATCH */
  --spectrum-tooltip-info-padding-bottom: 6px;
  --spectrum-tooltip-info-content-max-width: 126px;
  --spectrum-tooltip-negative-padding-bottom: 6px;
  --spectrum-tooltip-negative-content-max-width: 126px;
  --spectrum-tooltip-positive-padding-bottom: 6px;
  --spectrum-tooltip-positive-content-max-width: 126px;
}
